import {useEffect, useRef, useState} from 'react';
import {Condition, useIsMobile} from '@legit.health/ui';
import {
  Avatar,
  Box,
  List,
  ListItemIcon,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography
} from '@mui/material';
import {TFunction} from 'i18next';
import WebcamRecorder from '@/shared/components/form/WebcamRecorder';
import Mask from './components/Mask';
import TimeProgress from './components/TimeProgress';
import {StyledImage} from './hooks/styledComponents';
import {AVATAR, RecordState, PRIMARYTEXT, SECONDARYTEXT} from './utils';

export default function RecordVideo({
  condition,
  onChange,
  t
}: {
  onChange: (video: Blob | null) => void;
  t: TFunction;
  condition: Condition;
}) {
  const {pathology} = condition;
  const [currentStep, setCurrentStep] = useState(0);
  const [recordState, setRecordState] = useState(RecordState.Ready);
  const isMobile = useIsMobile();
  const stopRecordingRef = useRef<null | (() => void)>(null);

  function handleVideoReady(video: Blob) {
    setRecordState(RecordState.Recorded);
    onChange(video);
  }

  function handleClearVideo() {
    setRecordState(RecordState.Ready);
    setCurrentStep(0);
    onChange(null);
  }

  useEffect(
    function () {
      if (recordState !== RecordState.Recording) {
        return;
      }
      setTimeout(function () {
        if (currentStep === pathology.videoSteps.length - 1) {
          setRecordState(RecordState.Processing);
          if (stopRecordingRef.current) {
            stopRecordingRef.current();
          }
          return;
        } else {
          setCurrentStep((step) => step + 1);
        }
      }, 4000);
    },
    [currentStep, pathology.videoSteps.length, recordState]
  );

  function handleStartRecording(stopRecording: () => void) {
    setRecordState(RecordState.Recording);
    stopRecordingRef.current = stopRecording;
  }

  const videoStep = pathology.videoSteps[currentStep];

  useEffect(
    function () {
      if (!videoStep || videoStep.audio === null || recordState !== RecordState.Recording) {
        return;
      }
      const speechInstructions = new Audio(videoStep.audio);
      speechInstructions.play();
    },
    [videoStep, recordState]
  );

  return (
    <WebcamRecorder
      onClearVideo={handleClearVideo}
      onStartRecording={handleStartRecording}
      onVideoReady={handleVideoReady}
      enableAudioSource={false}
      mask={videoStep && <Mask stepImage={videoStep.image} />}
      t={t}
      title={
        <>
          {!isMobile && (
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'common.white',
                  fontWeight: 'bold'
                }}
              >
                {AVATAR(currentStep + 1)[recordState]}
              </Avatar>
            </ListItemAvatar>
          )}
          <ListItemText
            primary={t(PRIMARYTEXT[recordState])}
            secondary={
              videoStep && (
                <Typography variant="h2">
                  {t(SECONDARYTEXT[recordState](videoStep.text))}
                </Typography>
              )
            }
          />
        </>
      }
      rightColumnInitialComponent={
        <Box
          sx={{
            mb: 4
          }}
        >
          <List dense>
            {pathology.videoSteps.map((videoStep, index) => (
              <>
                {index === currentStep && recordState === RecordState.Recording && (
                  <TimeProgress duration={4000} />
                )}
                <ListItem
                  divider={index < pathology.videoSteps.length - 1}
                  sx={{
                    bgcolor: index === currentStep ? 'primary.main' : 'primary.light'
                  }}
                  key={videoStep.title}
                >
                  {videoStep.image && (
                    <ListItemIcon>
                      <StyledImage alt="instruction" src={videoStep.image} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    sx={{
                      color: index === currentStep ? 'white' : 'initial'
                    }}
                    secondary={<Typography sx={{color: 'inherit'}}>{videoStep.text}</Typography>}
                  />
                </ListItem>
              </>
            ))}
          </List>
        </Box>
      }
    />
  );
}
